import {
  ArrowLeftOutlined,
  DeleteOutlined,
  FilePdfOutlined,
  FileZipOutlined,
  PictureOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Table,
  Tabs,
  Typography,
  Upload,
} from 'antd';
import type { UploadChangeParam } from 'antd/es/upload';
import type { ColumnsType } from 'antd/es/table';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { MediaPickerModal } from '@/components/MediaPickerModal/MediaPickerModal';
import { RichTextEditor } from '@/components/RichTextEditor/RichTextEditor';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { BRAND } from '@/constants/brand';
import { ROUTES } from '@/constants/routes';
import { useNotify } from '@/hooks/useNotify';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useGetAttributeValuesQuery } from '@/modules/attribute-values/attributeValuesApi';
import { useGetAttributesQuery } from '@/modules/attributes/attributesApi';
import { useLazyGetBrandsQuery } from '@/modules/brands/brandsApi';
import { useLazyGetCategoriesQuery } from '@/modules/categories/categoriesApi';
import { useLazyGetCollectionsQuery } from '@/modules/collections/collectionsApi';
import {
  useGetAssembliesQuery,
  useGetConstituenciesQuery,
  useGetDistrictsQuery,
  useGetStatesQuery,
} from '@/modules/locations/locationsApi';
import {
  useCreateProductMutation,
  useGetProductQuery,
  useUpdateProductMutation,
  useUploadSamplePdfMutation,
  useUploadDigitalSamplePdfMutation,
  useUploadContentPdfMutation,
} from '@/modules/products/productsApi';
// import { useLazyGetSubcategoriesQuery } from '@/modules/subcategories/subcategoriesApi';
import type { ProductType, ProductVariant, ProductMediaRef, ShippingType } from '@/types/product';
import type { AttributeValue } from '@/types/attribute-value';
import type { MediaImage } from '@/types/media';
import { TAX_CLASS_OPTIONS } from '@/types/product';
import { resolveMutationResult } from '@/utils/apiResponse';
import {
  getContentPdfDisplayName,
  getContentPdfUrl,
  getDigitalSamplePdfDisplayName,
  getDigitalSamplePdfUrl,
  getMediaImageUrl,
  getSamplePdfDisplayName,
  getSamplePdfUrl,
} from '@/utils/media';
import { calculateDiscountFromPrices, syncProductPricing } from '@/utils/productPricing';
import { AttributeValuePicker } from './components/AttributeValuePicker';

const MONGO_ID_PATTERN = /^[a-f\d]{24}$/i;
const MAX_SAMPLE_PDF_SIZE = 20 * 1024 * 1024;
const MAX_DIGITAL_SAMPLE_ARCHIVE_SIZE = 100 * 1024 * 1024;
const MAX_CONTENT_PDF_SIZE = 20 * 1024 * 1024;

const PRICE_CATEGORY_ATTRIBUTE_KEYS = new Set([
  'price-category',
  'price-categories',
  'pricing-category',
  'pricing-categories',
]);

const FORMAT_ACCESS_ATTRIBUTE_KEYS = new Set([
  'format',
  'formats',
  'format-access',
  'format-access-type',
  'access',
  'access-type',
  'access-types',
]);

function attributeKey(attribute: { name: string; slug: string }) {
  return (attribute.slug || attribute.name).trim().toLowerCase().replace(/[\s_/]+/g, '-');
}

function pickMediaId(id?: string | null): string | undefined {
  const value = id?.trim();
  if (!value || !MONGO_ID_PATTERN.test(value)) return undefined;
  return value;
}

function pickMediaIds(ids: string[]): string[] {
  return [...new Set(ids.map((id) => pickMediaId(id)).filter(Boolean) as string[])];
}

function toPinnedMediaItem(ref: ProductMediaRef): MediaImage {
  return {
    id: ref.id,
    imageSmall: ref.imageSmall,
    imageLarge: ref.imageLarge,
    sortOrder: 0,
    isActive: true,
    createdAt: '',
    updatedAt: '',
  };
}

function buildAttributeValueStub(
  value: Pick<AttributeValue, 'id' | 'attributeId' | 'name' | 'slug'> & {
    colorCode?: string;
    description?: string;
  },
): AttributeValue {
  return {
    subtitle: '',
    mainDescription: '',
    whatsInside: '',
    editorialStandard: '',
    editorialSources: '',
    description: value.description ?? '',
    colorCode: value.colorCode,
    sortOrder: 0,
    infoTooltipEnabled: false,
    infoTooltipHtml: '',
    isActive: true,
    createdAt: '',
    updatedAt: '',
    id: value.id,
    attributeId: value.attributeId,
    name: value.name,
    slug: value.slug,
  };
}

function deriveAttributeIdsFromVariants(variantList: ProductVariant[]): string[] {
  const ids = new Set<string>();
  variantList.forEach((variant) => {
    variant.attributeValues?.forEach((value) => ids.add(value.attributeId));
  });
  return [...ids];
}

function mapVariantPayload(variant: ProductVariant, stockOverride?: number) {
  const synced = syncProductPricing(
    {
      regularPrice: variant.regularPrice,
      salePrice: variant.salePrice,
      discount: variant.discount,
    },
    variant.discount != null ? 'discount' : 'salePrice',
    variant.discount ?? variant.salePrice,
  );

  return {
    id: variant.id,
    attributeValueIds: variant.attributeValueIds,
    regularPrice: synced.regularPrice ?? variant.regularPrice,
    salePrice: synced.salePrice,
    discount: synced.discount,
    costPrice: variant.costPrice,
    shippingCharge: variant.shippingCharge ?? 0,
    stock: stockOverride ?? variant.stock,
    ...(pickMediaId(variant.imageMediaId)
      ? { imageMediaId: pickMediaId(variant.imageMediaId) }
      : {}),
    isActive: variant.isActive,
  };
}
import { buildProductSkuPreview, buildVariantSkuPreview } from '@/utils/productSku';
import { buildVariantKey, cartesianProduct, mergeVariants } from '@/utils/productVariants';
import { ucfirst, slugify } from '@/utils/string';
import { isColorAttribute, isImprintAttribute, isRegionAttribute, isStateAttribute } from '@/utils/attribute';
import styles from './ProductForm.module.scss';

const DROPDOWN_PARAMS = {
  page: 1,
  limit: 100,
  sortBy: 'name',
  sortOrder: 'asc' as const,
  status: 'active' as const,
};

function buildMultiSelectOptions(
  items: Array<{ id: string; name: string }>,
  selectedIds: string[],
  selectedNames?: string[],
) {
  const options = items.map((item) => ({ label: ucfirst(item.name), value: item.id }));
  const nameById = new Map(items.map((item) => [item.id, item.name]));

  if (selectedNames?.length) {
    selectedIds.forEach((id, index) => {
      const name = selectedNames[index];
      if (name && !nameById.has(id)) {
        nameById.set(id, name);
      }
    });
  }

  selectedIds.forEach((id) => {
    if (!options.some((option) => option.value === id)) {
      const name = nameById.get(id) ?? id;
      options.unshift({ label: ucfirst(name), value: id });
    }
  });

  return options;
}

type FactbookImprintKind =
  | 'district'
  | 'assembly'
  | 'constituency'
  | 'election-atlas'
  | 'coffee-table'
  | 'other';

function resolveFactbookImprintKind(imprintName: string): FactbookImprintKind {
  const normalized = imprintName.trim().toLowerCase().replace(/\s+/g, ' ');
  if (!normalized) return 'other';
  if (normalized.includes('election atlas') || normalized.includes('election-atlas')) {
    return 'election-atlas';
  }
  if (normalized.includes('coffee table') || normalized.includes('coffee-table')) {
    return 'coffee-table';
  }
  if (
    normalized.includes('constituency') ||
    normalized.includes('parliamentary') ||
    /\bpc\b/.test(normalized)
  ) {
    return 'constituency';
  }
  if (normalized.includes('assembly') || normalized.includes('ac fact')) {
    return 'assembly';
  }
  if (normalized.includes('district') || normalized.includes('dfb') || normalized.includes('factpage')) {
    return 'district';
  }
  return 'other';
}

/** Publication title = location name + selected imprint name. */
function buildFactbookPublicationTitle(params: {
  kind: FactbookImprintKind;
  imprintName: string;
  districtName?: string;
  assemblyName?: string;
  constituencyName?: string;
}): string {
  const imprintName = params.imprintName.trim();
  if (!imprintName) return '';

  if (params.kind === 'district') {
    const districtName = params.districtName?.trim();
    if (!districtName) return '';
    return `${districtName} ${imprintName}`;
  }

  if (params.kind === 'assembly') {
    const assemblyName = params.assemblyName?.trim();
    if (!assemblyName) return '';
    return `${assemblyName} ${imprintName}`;
  }

  if (params.kind === 'constituency') {
    const constituencyName = params.constituencyName?.trim();
    if (!constituencyName) return '';
    return `${constituencyName} ${imprintName}`;
  }

  return '';
}

interface ProductFormValues {
  name: string;
  slug: string;
  productType: ProductType;
  brandIds: string[];
  categoryIds: string[];
  subcategoryIds: string[];
  collectionIds: string[];
  stateId: string;
  districtId: string;
  assemblyId: string;
  constituencyId: string;
  shortDescription: string;
  description: string;
  seriesName: string;
  seriesNumber: string;
  editor: string;
  edition: string;
  isbn: string;
  doiUrl: string;
  deliveryTime: string;
  pageCount?: string;
  formatDetails: string;
  firstPublished: string;
  language: string;
  imprint: string;
  imprintValueId: string;
  weight: string;
  dimensions: string;
  apaCitation: string;
  chicagoCitation: string;
  whatsInside: string;
  editorialNote: string;
  tableOfContents: string;
  samplePdfUrl: string;
  digitalSamplePdfUrl: string;
  contentPdfUrl: string;
  returnExchangePolicy: string;
  shippingType: 'free' | 'paid';
  shippingCharge?: number;
  tags: string[];
  taxClass: string;
  regularPrice?: number;
  salePrice?: number;
  discount?: number;
  costPrice?: number;
  stock?: number;
  trackInventory: boolean;
  attributeIds: string[];
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string[];
  isActive: boolean;
}

interface PricingMatrixRow {
  key: string;
  priceCategoryValueId: string;
  formatValueId: string;
  priceCategory: string;
  format: string;
  regularPrice: number;
  salePrice?: number;
  discount?: number;
  shippingCharge: number;
}

type PricingMatrixOverride = Pick<
  PricingMatrixRow,
  'regularPrice' | 'salePrice' | 'discount' | 'shippingCharge'
>;

/** Shipping is allowed only for Format / Access types that include Print. */
function formatIncludesPrint(formatName: string): boolean {
  return /\bprint\b/i.test(formatName.trim());
}

function resolveMatrixShippingCharge(
  formatName: string,
  overrideShipping: number | undefined,
  variantShipping: number | undefined,
  productShippingFallback: number,
): number {
  if (!formatIncludesPrint(formatName)) return 0;
  if (overrideShipping != null) return overrideShipping;
  // Variant 0 used to block product-level shipping from bulk upload — fall back when unset/zero.
  if (variantShipping != null && variantShipping > 0) return variantShipping;
  if (productShippingFallback > 0) return productShippingFallback;
  return variantShipping ?? 0;
}

export default function ProductForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const notify = useNotify();

  const { data: product, isLoading: isLoadingProduct } = useGetProductQuery(id ?? '', {
    skip: !id,
    refetchOnMountOrArgChange: true,
  });
  const [createProduct, createState] = useCreateProductMutation();
  const [updateProduct, updateState] = useUpdateProductMutation();
  const [uploadSamplePdf, uploadSamplePdfState] = useUploadSamplePdfMutation();
  const [uploadDigitalSamplePdf, uploadDigitalSamplePdfState] =
    useUploadDigitalSamplePdfMutation();
  const [uploadContentPdf, uploadContentPdfState] = useUploadContentPdfMutation();

  const [variants, setVariants] = useState<ProductVariant[]>([]);
  const [thumbnailMediaId, setThumbnailMediaId] = useState<string | undefined>();
  const [imageMediaIds, setImageMediaIds] = useState<string[]>([]);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | undefined>();
  const [galleryPreviews, setGalleryPreviews] = useState<ProductMediaRef[]>([]);
  const [thumbPickerOpen, setThumbPickerOpen] = useState(false);
  const [galleryPickerOpen, setGalleryPickerOpen] = useState(false);
  const [selectedValueIds, setSelectedValueIds] = useState<string[]>([]);
  const [excludedVariantKeys, setExcludedVariantKeys] = useState<string[]>([]);
  const [pricingMatrixOverrides, setPricingMatrixOverrides] = useState<
    Record<string, PricingMatrixOverride>
  >({});
  const [samplePdfName, setSamplePdfName] = useState('');
  const [digitalSamplePdfName, setDigitalSamplePdfName] = useState('');
  const [contentPdfName, setContentPdfName] = useState('');
  const persistedVariantsRef = useRef<ProductVariant[]>([]);
  const persistedAttributeIdsRef = useRef<string[]>([]);
  const attributeSelectionTouchedRef = useRef(false);
  const hydratedProductIdRef = useRef<string | null>(null);
  const hydratedSnapshotRef = useRef('');

  const [fetchBrands, { data: brandsResult, isFetching: brandsLoading }] = useLazyGetBrandsQuery();
  const [fetchCategories, { data: categoriesResult, isFetching: categoriesLoading }] =
    useLazyGetCategoriesQuery();
  // const [fetchSubcategories, { data: subcategoriesResult, isFetching: subcategoriesLoading }] =
  //   useLazyGetSubcategoriesQuery();
  const [fetchCollections, { data: collectionsResult, isFetching: collectionsLoading }] =
    useLazyGetCollectionsQuery();

  const {
    data: attributesResult,
    isError: attributesLoadError,
    isFetching: attributesLoading,
    refetch: refetchAttributes,
  } = useGetAttributesQuery({
    page: 1,
    limit: 100,
    sortBy: 'sortOrder',
    sortOrder: 'asc',
    status: 'active',
  });

  const attributeValueQueryParams = useMemo(
    () => ({
      page: 1,
      limit: 100,
      sortBy: 'sortOrder' as const,
      sortOrder: 'asc' as const,
      status: 'active' as const,
    }),
    [],
  );

  const { control, handleSubmit, reset, watch, setValue } = useForm<ProductFormValues>({
    defaultValues: {
      name: '',
      slug: '',
      productType: 'variable',
      brandIds: [],
      categoryIds: [],
      subcategoryIds: [],
      collectionIds: [],
      stateId: '',
      districtId: '',
      assemblyId: '',
      constituencyId: '',
      shortDescription: '',
      description: '',
      seriesName: '',
      seriesNumber: '',
      editor: '',
      edition: '',
      isbn: '',
      doiUrl: '',
      deliveryTime: 'Within 5-7 working days in India',
      pageCount: '',
      formatDetails: '',
      firstPublished: '',
      language: 'English',
      imprint: '',
      imprintValueId: '',
      weight: '',
      dimensions: '',
      apaCitation: '',
      chicagoCitation: '',
      whatsInside: '',
      editorialNote: '',
      tableOfContents: '',
      samplePdfUrl: '',
      digitalSamplePdfUrl: '',
      contentPdfUrl: '',
      returnExchangePolicy: '',
      shippingType: 'free',
      shippingCharge: 0,
      tags: [],
      taxClass: '',
      stock: 0,
      trackInventory: true,
      attributeIds: [],
      metaTitle: '',
      metaDescription: '',
      metaKeywords: [],
      isActive: true,
    },
  });

  const productType = watch('productType');
  const effectiveProductType = productType;
  const brandIds = watch('brandIds');
  const categoryIds = watch('categoryIds');
  const collectionIds = watch('collectionIds');
  const selectedStateId = watch('stateId');
  const selectedDistrictId = watch('districtId');
  const selectedAssemblyId = watch('assemblyId');
  const selectedConstituencyId = watch('constituencyId');
  const name = watch('name');
  const imprintValueId = watch('imprintValueId');
  const imprintName = watch('imprint');
  const seriesName = watch('seriesName');
  const autoSlug = useMemo(() => slugify(name), [name]);
  const skuPreview = useMemo(() => {
    if (isEdit && product?.sku) return product.sku;
    if (!name.trim()) return '';
    return buildProductSkuPreview(name);
  }, [isEdit, name, product?.sku]);

  const brandOptions = useMemo(
    () =>
      buildMultiSelectOptions(
        brandsResult?.results ?? [],
        isEdit ? (product?.brandIds ?? brandIds) : brandIds,
        product?.brandNames,
      ),
    [brandIds, brandsResult?.results, isEdit, product?.brandIds, product?.brandNames],
  );

  const categoryOptions = useMemo(
    () =>
      buildMultiSelectOptions(
        categoriesResult?.results ?? [],
        isEdit ? (product?.categoryIds ?? categoryIds) : categoryIds,
        product?.categoryNames,
      ),
    [categoriesResult?.results, categoryIds, isEdit, product?.categoryIds, product?.categoryNames],
  );

  /*
  const subcategoryOptions = useMemo(
    () =>
      buildMultiSelectOptions(
        (subcategoriesResult?.results ?? []).filter(
          (item) => !categoryIds.length || categoryIds.includes(item.categoryId),
        ),
        isEdit ? (product?.subcategoryIds ?? subcategoryIds) : subcategoryIds,
        product?.subcategoryNames,
      ),
    [
      categoryIds,
      isEdit,
      product?.subcategoryIds,
      product?.subcategoryNames,
      subcategoriesResult?.results,
      subcategoryIds,
    ],
  );
  */

  const collectionOptions = useMemo(
    () =>
      buildMultiSelectOptions(
        collectionsResult?.results ?? [],
        isEdit ? (product?.collectionIds ?? collectionIds) : collectionIds,
        product?.collectionNames,
      ),
    [
      collectionIds,
      collectionsResult?.results,
      isEdit,
      product?.collectionIds,
      product?.collectionNames,
    ],
  );

  const { data: statesResult, isFetching: statesLoading } = useGetStatesQuery({ status: 'active' });
  const { data: districtsResult, isFetching: districtsLoading } = useGetDistrictsQuery({
    stateId: selectedStateId || undefined,
    status: 'active',
  });

  const stateOptions = useMemo(
    () => (statesResult?.results ?? []).map((state) => ({ label: state.name, value: state.id })),
    [statesResult?.results],
  );

  const districtOptions = useMemo(
    () =>
      selectedStateId
        ? (districtsResult?.results ?? []).map((district) => ({
            label: district.name,
            value: district.id,
          }))
        : [],
    [districtsResult?.results, selectedStateId],
  );

  const selectedDistrictName = useMemo(
    () => districtOptions.find((option) => option.value === selectedDistrictId)?.label ?? '',
    [districtOptions, selectedDistrictId],
  );

  useEffect(() => {
    setValue('slug', autoSlug);
  }, [autoSlug, setValue]);

  const {
    data: attributeValuesResult,
    isFetching: attributeValuesLoading,
    isError: attributeValuesLoadError,
    refetch: refetchAttributeValues,
  } = useGetAttributeValuesQuery(attributeValueQueryParams);

  const allAttributeValues = useMemo(() => {
    const values = attributeValuesResult?.results ?? [];
    const map = new Map(values.map((value) => [value.id, value]));

    (product?.variants ?? []).forEach((variant) => {
      variant.attributeValueIds.forEach((valueId) => {
        if (map.has(valueId)) return;

        const fromVariant = variant.attributeValues?.find((item) => item.id === valueId);
        if (!fromVariant) return;

        map.set(valueId, buildAttributeValueStub({
          id: fromVariant.id,
          attributeId: fromVariant.attributeId,
          name: fromVariant.name,
          slug: fromVariant.slug,
          colorCode: fromVariant.colorCode,
        }));
      });

      variant.attributeValues?.forEach((value) => {
        if (!map.has(value.id)) {
          map.set(value.id, buildAttributeValueStub({
            id: value.id,
            attributeId: value.attributeId,
            name: value.name,
            slug: value.slug,
            colorCode: value.colorCode,
          }));
        }
      });
    });

    return [...map.values()];
  }, [attributeValuesResult?.results, product?.variants]);

  const allAttributes = useMemo(() => {
    const attributes = attributesResult?.results ?? [];
    const map = new Map(attributes.map((attribute) => [attribute.id, attribute]));

    product?.attributeIds?.forEach((attributeId, index) => {
      if (!map.has(attributeId)) {
        map.set(attributeId, {
          id: attributeId,
          name: product.attributeNames?.[index] ?? attributeId,
          slug: '',
          description: '',
          sortOrder: 0,
          isActive: true,
          createdAt: '',
          updatedAt: '',
        });
      }
    });

    return [...map.values()];
  }, [attributesResult?.results, product?.attributeIds, product?.attributeNames]);

  const imprintAttribute = useMemo(
    () => allAttributes.find((attribute) => isImprintAttribute(attribute)) ?? null,
    [allAttributes],
  );

  const priceCategoryAttribute = useMemo(
    () =>
      allAttributes.find((attribute) =>
        PRICE_CATEGORY_ATTRIBUTE_KEYS.has(attributeKey(attribute)),
      ) ?? null,
    [allAttributes],
  );

  const formatAccessAttribute = useMemo(
    () =>
      allAttributes.find((attribute) =>
        FORMAT_ACCESS_ATTRIBUTE_KEYS.has(attributeKey(attribute)),
      ) ?? null,
    [allAttributes],
  );

  const imprintValueOptions = useMemo(
    () =>
      allAttributeValues
        .filter((value) => String(value.attributeId) === String(imprintAttribute?.id ?? ''))
        .map((value) => ({ label: value.name, value: value.id })),
    [allAttributeValues, imprintAttribute?.id],
  );

  const resolvedImprintName = useMemo(() => {
    const fromOptions = imprintValueOptions.find((option) => option.value === imprintValueId)?.label;
    return (fromOptions || imprintName || '').trim();
  }, [imprintName, imprintValueId, imprintValueOptions]);

  const imprintKind = useMemo(
    () => resolveFactbookImprintKind(resolvedImprintName),
    [resolvedImprintName],
  );
  /** Title kind: imprint → series name → selected location fields. */
  const titleKind = useMemo(() => {
    if (imprintKind !== 'other' && imprintKind !== 'coffee-table') return imprintKind;
    const fromSeries = resolveFactbookImprintKind(seriesName || '');
    if (fromSeries !== 'other' && fromSeries !== 'coffee-table') return fromSeries;
    if (selectedConstituencyId) return 'constituency' as const;
    if (selectedAssemblyId) return 'assembly' as const;
    if (selectedDistrictId) return 'district' as const;
    return imprintKind === 'coffee-table' ? ('coffee-table' as const) : ('other' as const);
  }, [
    imprintKind,
    selectedAssemblyId,
    selectedConstituencyId,
    selectedDistrictId,
    seriesName,
  ]);
  const isElectionAtlasImprint = imprintKind === 'election-atlas';
  const isCoffeeTableImprint = imprintKind === 'coffee-table';
  const isLocationFactbook =
    imprintKind === 'district' ||
    imprintKind === 'assembly' ||
    imprintKind === 'constituency';
  const canAutoFillTitle =
    titleKind === 'district' || titleKind === 'assembly' || titleKind === 'constituency';
  const isTitleManual = isCoffeeTableImprint || imprintKind === 'other';
  // Election Atlas of India has no location hierarchy — name is the imprint only.
  const stateEnabled = !isElectionAtlasImprint;
  const districtEnabled =
    !isElectionAtlasImprint && (!isLocationFactbook || imprintKind === 'district');
  // Assembly Factbook: State → Assembly (Constituency optional). No district.
  // Constituency Factbook: State → Constituency. No district.
  const assemblyEnabled =
    !isElectionAtlasImprint && (!isLocationFactbook || imprintKind === 'assembly');
  const constituencyEnabled =
    !isElectionAtlasImprint &&
    (!isLocationFactbook || imprintKind === 'constituency' || imprintKind === 'assembly');

  const { data: assembliesResult, isFetching: assembliesLoading } = useGetAssembliesQuery({
    stateId: selectedStateId || undefined,
    districtId: districtEnabled ? selectedDistrictId || undefined : undefined,
    constituencyId: selectedConstituencyId || undefined,
    status: 'active',
  });
  const { data: constituenciesResult, isFetching: constituenciesLoading } =
    useGetConstituenciesQuery({
      stateId: selectedStateId || undefined,
      districtId: districtEnabled ? selectedDistrictId || undefined : undefined,
      status: 'active',
    });

  const assemblyOptions = useMemo(
    () =>
      selectedStateId
        ? (assembliesResult?.results ?? []).map((assembly) => ({
            label: assembly.name,
            value: assembly.id,
          }))
        : [],
    [assembliesResult?.results, selectedStateId],
  );

  const constituencyOptions = useMemo(
    () =>
      selectedStateId
        ? (constituenciesResult?.results ?? []).map((constituency) => ({
            label: constituency.name,
            value: constituency.id,
          }))
        : [],
    [constituenciesResult?.results, selectedStateId],
  );

  const selectedAssemblyName = useMemo(
    () => assemblyOptions.find((option) => option.value === selectedAssemblyId)?.label ?? '',
    [assemblyOptions, selectedAssemblyId],
  );
  const selectedConstituencyName = useMemo(
    () =>
      constituencyOptions.find((option) => option.value === selectedConstituencyId)?.label ?? '',
    [constituencyOptions, selectedConstituencyId],
  );

  useEffect(() => {
    if (!imprintValueId) return;
    const fromOptions = imprintValueOptions.find((option) => option.value === imprintValueId)?.label;
    if (fromOptions && fromOptions !== imprintName) {
      setValue('imprint', fromOptions);
    }
  }, [imprintName, imprintValueId, imprintValueOptions, setValue]);

  // Election Atlas: publication title = imprint name only.
  useEffect(() => {
    if (!isElectionAtlasImprint || !resolvedImprintName) return;
    if (name !== resolvedImprintName) {
      setValue('name', resolvedImprintName, { shouldDirty: true, shouldValidate: true });
    }
  }, [isElectionAtlasImprint, name, resolvedImprintName, setValue]);

  useEffect(() => {
    if (!canAutoFillTitle || !resolvedImprintName) return;

    const nextTitle = buildFactbookPublicationTitle({
      kind: titleKind,
      imprintName: resolvedImprintName,
      districtName: selectedDistrictName,
      assemblyName: selectedAssemblyName,
      constituencyName: selectedConstituencyName,
    });

    if (nextTitle && nextTitle !== name) {
      setValue('name', nextTitle, { shouldDirty: true, shouldValidate: true });
    }
  }, [
    canAutoFillTitle,
    name,
    resolvedImprintName,
    selectedAssemblyName,
    selectedConstituencyName,
    selectedDistrictName,
    setValue,
    titleKind,
  ]);

  useEffect(() => {
    if (!isLocationFactbook && !isElectionAtlasImprint) return;
    if (!stateEnabled && selectedStateId) setValue('stateId', '');
    if (!districtEnabled && selectedDistrictId) setValue('districtId', '');
    if (!assemblyEnabled && selectedAssemblyId) setValue('assemblyId', '');
    if (!constituencyEnabled && selectedConstituencyId) setValue('constituencyId', '');
  }, [
    assemblyEnabled,
    constituencyEnabled,
    districtEnabled,
    isElectionAtlasImprint,
    isLocationFactbook,
    selectedAssemblyId,
    selectedConstituencyId,
    selectedDistrictId,
    selectedStateId,
    setValue,
    stateEnabled,
  ]);

  useEffect(() => {
    if (!isEdit || imprintValueId || !imprintAttribute?.id) return;

    const legacyVariantValue = product?.variants
      ?.flatMap((variant) => variant.attributeValues ?? [])
      .find((value) => String(value.attributeId) === String(imprintAttribute.id));
    const legacyTextValue = allAttributeValues.find(
      (value) =>
        String(value.attributeId) === String(imprintAttribute.id) &&
        (value.name.toLowerCase() === product?.imprint?.trim().toLowerCase() ||
          value.slug === slugify(product?.imprint ?? '')),
    );
    const resolved = legacyVariantValue?.id ?? legacyTextValue?.id;
    if (!resolved) return;

    setValue('imprintValueId', resolved);
    setValue('imprint', legacyVariantValue?.name ?? legacyTextValue?.name ?? '');
  }, [
    allAttributeValues,
    imprintAttribute?.id,
    imprintValueId,
    isEdit,
    product?.imprint,
    product?.variants,
    setValue,
  ]);

  const attributePickerGroups = useMemo(
    () =>
      allAttributes
        .filter(
          (attribute) =>
            !isImprintAttribute(attribute) &&
            !isStateAttribute(attribute) &&
            !isRegionAttribute(attribute),
        )
        .map((attribute) => {
          const values = allAttributeValues.filter(
            (value) => String(value.attributeId) === String(attribute.id),
          );
          if (!values.length) return null;

          const isColour = isColorAttribute(attribute);

          return {
            attributeId: attribute.id,
            attributeName: attribute.name,
            isColour,
            options: values.map((value) => ({
              valueId: value.id,
              name: value.name,
              colorCode: value.colorCode,
            })),
          };
        })
        .filter((group): group is NonNullable<typeof group> => Boolean(group)),
    [allAttributeValues, allAttributes],
  );

  const selectedValueIdsByAttribute = useMemo(() => {
    const map: Record<string, string[]> = {};

    selectedValueIds.forEach((valueId) => {
      const value = allAttributeValues.find((item) => item.id === valueId);
      if (!value) return;

      const attrId = String(value.attributeId);
      if (!map[attrId]) map[attrId] = [];
      map[attrId].push(valueId);
    });

    return map;
  }, [allAttributeValues, selectedValueIds]);

  const canDeriveAttributes = useMemo(() => {
    if (!selectedValueIds.length) return true;
    return selectedValueIds.every((valueId) =>
      allAttributeValues.some((value) => value.id === valueId),
    );
  }, [allAttributeValues, selectedValueIds]);

  const derivedAttributeIds = useMemo(() => {
    const selectedAttrIds = new Set(Object.keys(selectedValueIdsByAttribute));
    const fromSelection = allAttributes
      .filter((attribute) => selectedAttrIds.has(String(attribute.id)))
      .map((attribute) => attribute.id);

    if (fromSelection.length) return fromSelection;

    if (
      isEdit &&
      product?.attributeIds?.length &&
      selectedValueIds.length &&
      !canDeriveAttributes
    ) {
      return product.attributeIds;
    }

    return fromSelection;
  }, [
    allAttributes,
    canDeriveAttributes,
    isEdit,
    product?.attributeIds,
    selectedValueIds.length,
    selectedValueIdsByAttribute,
  ]);

  useEffect(() => {
    if (derivedAttributeIds.length) {
      setValue('attributeIds', derivedAttributeIds);
      return;
    }

    if (
      isEdit &&
      product?.attributeIds?.length &&
      selectedValueIds.length &&
      !canDeriveAttributes
    ) {
      setValue('attributeIds', product.attributeIds);
    }
  }, [
    canDeriveAttributes,
    derivedAttributeIds,
    isEdit,
    product?.attributeIds,
    selectedValueIds.length,
    setValue,
  ]);

  useEffect(() => {
    const hasVariantSelection = selectedValueIds.length > 0 || variants.length > 0;
    const autoProductType: ProductType = hasVariantSelection
      ? 'variable'
      : isEdit
        ? (product?.productType ?? 'simple')
        : 'variable';

    if (productType !== autoProductType) {
      setValue('productType', autoProductType);
    }
  }, [isEdit, product?.productType, productType, selectedValueIds.length, setValue, variants.length]);

  const deriveSelectedValueIdsFromVariants = (productVariants: ProductVariant[]) => {
    const ids = new Set<string>();
    productVariants.forEach((variant) => {
      variant.attributeValueIds.forEach((valueId) => {
        const value = allAttributeValues.find((item) => item.id === valueId);
        const attribute = allAttributes.find((item) => String(item.id) === String(value?.attributeId ?? ''));
        if (
          value &&
          ((imprintAttribute?.id && String(value.attributeId) === String(imprintAttribute.id)) ||
            isStateAttribute(attribute) ||
            isRegionAttribute(attribute))
        ) {
          return;
        }
        ids.add(valueId);
      });
    });
    return [...ids];
  };

  const productSkuBase = useMemo(
    () => (isEdit && product?.sku ? product.sku : buildProductSkuPreview(name)),
    [isEdit, name, product?.sku],
  );

  const buildSelectedVariants = useCallback((): ProductVariant[] => {
    if (!derivedAttributeIds.length) return [];

    const valueGroups = derivedAttributeIds.map(
      (attrId) => selectedValueIdsByAttribute[attrId] ?? [],
    );
    if (valueGroups.some((group) => !group.length)) return [];

    const excluded = new Set(excludedVariantKeys);
    return cartesianProduct(valueGroups)
      .filter((attributeValueIds) => !excluded.has(buildVariantKey(attributeValueIds)))
      .map((attributeValueIds) => {
        const attributeValues = attributeValueIds
          .map((valueId) => allAttributeValues.find((value) => value.id === valueId))
          .filter((value): value is NonNullable<typeof value> => value !== undefined)
          .map((value) => ({
            id: value.id,
            attributeId: value.attributeId,
            name: value.name,
            slug: value.slug,
            colorCode: value.colorCode,
          }));

        return {
          attributeValueIds,
          attributeValues,
          sku: buildVariantSkuPreview(
            productSkuBase,
            attributeValues.map((value) => value.slug),
          ),
          regularPrice: 0,
          shippingCharge: 0,
          stock: 0,
          isActive: true,
        };
      });
  }, [
    allAttributeValues,
    derivedAttributeIds,
    excludedVariantKeys,
    productSkuBase,
    selectedValueIdsByAttribute,
  ]);

  const getVariantValueByAttribute = useCallback(
    (variant: ProductVariant, attributeId?: string | null) => {
      if (!attributeId) return undefined;

      return (
        variant.attributeValues?.find((value) => String(value.attributeId) === String(attributeId)) ??
        allAttributeValues.find(
          (value) =>
            String(value.attributeId) === String(attributeId) &&
            variant.attributeValueIds.includes(value.id),
        )
      );
    },
    [allAttributeValues],
  );

  const productShippingFallback = Number(watch('shippingCharge') ?? 0);

  const pricingMatrixRows = useMemo<PricingMatrixRow[]>(() => {
    if (!priceCategoryAttribute?.id || !formatAccessAttribute?.id) return [];

    const rows = new Map<string, PricingMatrixRow>();
    const selectedPriceCategoryIds =
      selectedValueIdsByAttribute[String(priceCategoryAttribute.id)] ?? [];
    const selectedFormatIds = selectedValueIdsByAttribute[String(formatAccessAttribute.id)] ?? [];

    selectedPriceCategoryIds.forEach((priceCategoryId) => {
      const priceCategory = allAttributeValues.find((value) => value.id === priceCategoryId);
      if (!priceCategory) return;

      selectedFormatIds.forEach((formatId) => {
        const format = allAttributeValues.find((value) => value.id === formatId);
        if (!format) return;

        const matchingVariant = variants.find(
          (variant) =>
            variant.attributeValueIds.includes(priceCategoryId) &&
            variant.attributeValueIds.includes(formatId),
        );
        const key = `${priceCategory.id}:${format.id}`;
        const override = pricingMatrixOverrides[key];

        rows.set(key, {
          key,
          priceCategoryValueId: priceCategory.id,
          formatValueId: format.id,
          priceCategory: priceCategory.name,
          format: format.name,
          regularPrice: override?.regularPrice ?? matchingVariant?.regularPrice ?? 0,
          salePrice: override ? override.salePrice : matchingVariant?.salePrice,
          discount: override ? override.discount : matchingVariant?.discount,
          shippingCharge: resolveMatrixShippingCharge(
            format.name,
            override?.shippingCharge,
            matchingVariant?.shippingCharge,
            productShippingFallback,
          ),
        });
      });
    });

    variants.forEach((variant) => {
      const priceCategory = getVariantValueByAttribute(variant, priceCategoryAttribute.id);
      const format = getVariantValueByAttribute(variant, formatAccessAttribute.id);
      if (!priceCategory?.id || !format?.id) return;

      const key = `${priceCategory.id}:${format.id}`;
      if (rows.has(key)) return;
      const override = pricingMatrixOverrides[key];

      rows.set(key, {
        key,
        priceCategoryValueId: priceCategory.id,
        formatValueId: format.id,
        priceCategory: priceCategory.name,
        format: format.name,
        regularPrice: override?.regularPrice ?? variant.regularPrice,
        salePrice: override ? override.salePrice : variant.salePrice,
        discount: override ? override.discount : variant.discount,
        shippingCharge: resolveMatrixShippingCharge(
          format.name,
          override?.shippingCharge,
          variant.shippingCharge,
          productShippingFallback,
        ),
      });
    });

    return [...rows.values()];
  }, [
    allAttributeValues,
    formatAccessAttribute?.id,
    getVariantValueByAttribute,
    priceCategoryAttribute?.id,
    pricingMatrixOverrides,
    productShippingFallback,
    selectedValueIdsByAttribute,
    variants,
  ]);

  const hasPrintFormatSelected = useMemo(
    () => pricingMatrixRows.some((row) => formatIncludesPrint(row.format)),
    [pricingMatrixRows],
  );

  const updatePricingMatrixRow = useCallback(
    (
      row: PricingMatrixRow,
      changed: 'regularPrice' | 'salePrice' | 'discount' | 'shippingCharge',
      value?: number | null,
    ) => {
      if (changed === 'shippingCharge') {
        const nextShipping = formatIncludesPrint(row.format)
          ? Math.round(Number(value ?? 0))
          : 0;
        setPricingMatrixOverrides((current) => ({
          ...current,
          [row.key]: {
            regularPrice: row.regularPrice,
            salePrice: row.salePrice,
            discount: row.discount,
            shippingCharge: nextShipping,
          },
        }));
        setVariants((current) =>
          current.map((variant) => {
            const hasPriceCategory = variant.attributeValueIds.includes(row.priceCategoryValueId);
            const hasFormat = variant.attributeValueIds.includes(row.formatValueId);
            if (!hasPriceCategory || !hasFormat) return variant;
            return { ...variant, shippingCharge: nextShipping };
          }),
        );
        return;
      }

      const nextPricing = syncProductPricing(row, changed, value);
      setPricingMatrixOverrides((current) => ({
        ...current,
        [row.key]: {
          regularPrice: nextPricing.regularPrice ?? 0,
          salePrice: nextPricing.salePrice,
          discount: nextPricing.discount,
          shippingCharge: row.shippingCharge,
        },
      }));
      setVariants((current) =>
        current.map((variant) => {
          const hasPriceCategory = variant.attributeValueIds.includes(row.priceCategoryValueId);
          const hasFormat = variant.attributeValueIds.includes(row.formatValueId);
          if (!hasPriceCategory || !hasFormat) return variant;

          return {
            ...syncProductPricing(variant, changed, value),
            shippingCharge: variant.shippingCharge ?? row.shippingCharge,
          };
        }),
      );
    },
    [],
  );

  const getPricingCurrencyPrefix = (row: PricingMatrixRow) =>
    row.priceCategory.trim().toLowerCase() === 'other than india' ? '$' : '₹';

  const applyPricingMatrixOverride = useCallback(
    (variant: ProductVariant): ProductVariant => {
      if (!priceCategoryAttribute?.id || !formatAccessAttribute?.id) return variant;

      const priceCategoryValue = getVariantValueByAttribute(variant, priceCategoryAttribute.id);
      const formatValue = getVariantValueByAttribute(variant, formatAccessAttribute.id);
      if (!priceCategoryValue?.id || !formatValue?.id) return variant;

      const override = pricingMatrixOverrides[`${priceCategoryValue.id}:${formatValue.id}`];
      if (!override) return variant;

      return {
        ...variant,
        regularPrice: override.regularPrice,
        salePrice: override.salePrice,
        discount: override.discount,
        shippingCharge: formatIncludesPrint(formatValue.name)
          ? (override.shippingCharge ?? 0)
          : 0,
      };
    },
    [
      formatAccessAttribute?.id,
      getVariantValueByAttribute,
      priceCategoryAttribute?.id,
      pricingMatrixOverrides,
    ],
  );

  const pricingColumns = useMemo<ColumnsType<PricingMatrixRow>>(
    () => [
      {
        title: 'Price category',
        key: 'priceCategory',
        dataIndex: 'priceCategory',
        width: 140,
        ellipsis: true,
      },
      {
        title: 'Format / Access Type',
        key: 'format',
        width: 240,
        render: (_: unknown, row) => (
          <span className={styles.priceMatrixFormat}>{row.format}</span>
        ),
      },
      {
        title: 'Regular price',
        key: 'regularPrice',
        width: 120,
        render: (_: unknown, row) => (
          <InputNumber
            min={0}
            precision={0}
            size="small"
            className={styles.variantNumber}
            prefix={getPricingCurrencyPrefix(row)}
            value={row.regularPrice}
            onChange={(value) => updatePricingMatrixRow(row, 'regularPrice', value)}
          />
        ),
      },
      {
        title: 'Discount %',
        key: 'discount',
        width: 100,
        render: (_: unknown, row) => (
          <InputNumber
            min={0}
            max={100}
            size="small"
            className={styles.variantNumber}
            value={row.discount}
            onChange={(value) => updatePricingMatrixRow(row, 'discount', value)}
          />
        ),
      },
      {
        title: 'Sale price',
        key: 'salePrice',
        width: 120,
        render: (_: unknown, row) => (
          <InputNumber
            min={0}
            precision={0}
            size="small"
            className={styles.variantNumber}
            prefix={getPricingCurrencyPrefix(row)}
            value={row.salePrice}
            onChange={(value) => updatePricingMatrixRow(row, 'salePrice', value)}
          />
        ),
      },
      {
        title: 'Shipping',
        key: 'shippingCharge',
        width: 120,
        fixed: 'right',
        render: (_: unknown, row) => {
          if (!formatIncludesPrint(row.format)) {
            return <Typography.Text type="secondary">—</Typography.Text>;
          }

          return (
            <InputNumber
              min={0}
              precision={0}
              size="small"
              className={styles.variantNumber}
              prefix={getPricingCurrencyPrefix(row)}
              value={row.shippingCharge}
              onChange={(value) => updatePricingMatrixRow(row, 'shippingCharge', value)}
            />
          );
        },
      },
    ],
    [updatePricingMatrixRow],
  );

  useEffect(() => {
    hydratedProductIdRef.current = null;
    hydratedSnapshotRef.current = '';
    attributeSelectionTouchedRef.current = false;
    persistedVariantsRef.current = [];
    persistedAttributeIdsRef.current = [];
  }, [id]);

  useLayoutEffect(() => {
    if (!isEdit || !product?.id) return;

    const snapshot = `${product.id}:${product.updatedAt}:${(product.variants ?? [])
      .map((variant) => variant.id ?? buildVariantKey(variant.attributeValueIds))
      .join('|')}`;

    if (hydratedSnapshotRef.current === snapshot) return;

    hydratedSnapshotRef.current = snapshot;
    hydratedProductIdRef.current = product.id;
    attributeSelectionTouchedRef.current = false;

    const loadedVariants = product.variants ?? [];
    persistedVariantsRef.current = loadedVariants;
    persistedAttributeIdsRef.current = product.attributeIds ?? [];

    reset({
      name: product.name,
      slug: product.slug,
      productType: product.productType,
      brandIds: product.brandIds ?? [],
      categoryIds: product.categoryIds ?? [],
      subcategoryIds: product.subcategoryIds ?? [],
      collectionIds: product.collectionIds ?? [],
      stateId: product.stateId ?? '',
      districtId: product.districtId ?? '',
      assemblyId: product.assemblyId ?? '',
      constituencyId: product.constituencyId ?? '',
      shortDescription: product.shortDescription ?? '',
      description: product.description ?? '',
      seriesName: product.seriesName ?? '',
      seriesNumber: product.seriesNumber ?? '',
      editor: product.editor ?? '',
      edition: product.edition ?? '',
      isbn: product.isbn ?? '',
      doiUrl: product.doiUrl ?? '',
      deliveryTime: product.deliveryTime ?? '',
      pageCount: product.pageCount ?? '',
      formatDetails: product.formatDetails ?? '',
      firstPublished: product.firstPublished ?? '',
      language: product.language ?? '',
      imprint: product.imprint ?? '',
      imprintValueId: product.imprintValueId ?? '',
      weight: product.weight ?? '',
      dimensions: product.dimensions ?? '',
      apaCitation: product.apaCitation ?? '',
      chicagoCitation: product.chicagoCitation ?? '',
      whatsInside: product.whatsInside ?? '',
      editorialNote: product.editorialNote ?? '',
      tableOfContents: product.tableOfContents ?? '',
      samplePdfUrl: product.samplePdfUrl ?? '',
      digitalSamplePdfUrl: product.digitalSamplePdfUrl ?? '',
      contentPdfUrl: product.contentPdfUrl ?? '',
      returnExchangePolicy: product.returnExchangePolicy ?? '',
      shippingType: product.shippingType ?? 'free',
      shippingCharge: product.shippingCharge ?? 0,
      tags: product.tags ?? [],
      taxClass: product.taxClass ?? '',
      regularPrice: product.regularPrice,
      salePrice: product.salePrice,
      discount:
        product.discount ?? calculateDiscountFromPrices(product.regularPrice, product.salePrice),
      costPrice: product.costPrice,
      stock: product.stock,
      trackInventory: product.trackInventory ?? true,
      attributeIds: product.attributeIds ?? [],
      metaTitle: product.metaTitle ?? '',
      metaDescription: product.metaDescription ?? '',
      metaKeywords: product.metaKeywords ?? [],
      isActive: product.isActive,
    });

    setSelectedValueIds(deriveSelectedValueIdsFromVariants(loadedVariants));
    setExcludedVariantKeys([]);
    setPricingMatrixOverrides({});
    const legacyProductShipping = product.shippingCharge ?? 0;
    setVariants(
      loadedVariants.map((variant) => {
        const includesPrint =
          variant.attributeValues?.some((value) => formatIncludesPrint(value.name)) ?? false;
        const variantShipping = variant.shippingCharge ?? 0;
        return {
          ...variant,
          shippingCharge: includesPrint
            ? variantShipping > 0
              ? variantShipping
              : legacyProductShipping
            : 0,
        };
      }),
    );
    setThumbnailMediaId(product.thumbnailMediaId ?? undefined);
    setImageMediaIds(product.imageMediaIds ?? []);
    setThumbnailPreview(
      getMediaImageUrl(product.thumbnail?.imageLarge ?? product.thumbnail?.imageSmall),
    );
    setGalleryPreviews(product.images ?? []);
    setSamplePdfName(getSamplePdfDisplayName(product.samplePdfUrl));
    setDigitalSamplePdfName(getDigitalSamplePdfDisplayName(product.digitalSamplePdfUrl));
    setContentPdfName(getContentPdfDisplayName(product.contentPdfUrl));
  }, [isEdit, product, reset]);

  useEffect(() => {
    if (!isEdit || !product) return;

    void fetchBrands(DROPDOWN_PARAMS);
    void fetchCategories(DROPDOWN_PARAMS);
    void fetchCollections(DROPDOWN_PARAMS);
    // void fetchSubcategories({
    //   ...DROPDOWN_PARAMS,
    //   ...(product.categoryIds?.length ? { categoryIds: product.categoryIds } : {}),
    // });
  }, [fetchBrands, fetchCategories, fetchCollections, isEdit, product]);

  useEffect(() => {
    if (isEdit && product?.id && hydratedProductIdRef.current !== product.id) {
      return;
    }

    if (effectiveProductType !== 'variable') {
      if (!isEdit || !persistedVariantsRef.current.length) {
        setVariants([]);
      }
      return;
    }

    if (isEdit && !attributeSelectionTouchedRef.current) {
      return;
    }

    if (isEdit && selectedValueIds.length && !canDeriveAttributes) {
      return;
    }

    if (!derivedAttributeIds.length) {
      if (isEdit && (variants.length || persistedVariantsRef.current.length)) {
        return;
      }
      if (!isEdit || !selectedValueIds.length) {
        setVariants([]);
      }
      return;
    }

    const valueGroups = derivedAttributeIds.map(
      (attrId) => selectedValueIdsByAttribute[attrId] ?? [],
    );
    if (valueGroups.some((group) => !group.length)) {
      if (isEdit && (variants.length || persistedVariantsRef.current.length)) {
        return;
      }
      setVariants([]);
      return;
    }

    const generated = buildSelectedVariants();

    setVariants((prev) => {
      const generatedKeys = new Set(
        generated.map((variant) => buildVariantKey(variant.attributeValueIds)),
      );

      return mergeVariants(generated, prev)
        .filter((variant) => generatedKeys.has(buildVariantKey(variant.attributeValueIds)))
        .map((variant) => {
          const slugs =
            variant.attributeValues?.map((value) => value.slug) ??
            variant.attributeValueIds
              .map((valueId) => allAttributeValues.find((item) => item.id === valueId)?.slug ?? '')
              .filter(Boolean);

          return {
            ...variant,
            sku: buildVariantSkuPreview(productSkuBase, slugs),
            regularPrice: variant.regularPrice ?? 0,
            shippingCharge: variant.shippingCharge ?? 0,
            stock: variant.stock ?? 0,
            isActive: variant.isActive ?? true,
          };
        });
    });
  }, [
    allAttributeValues,
    buildSelectedVariants,
    canDeriveAttributes,
    derivedAttributeIds,
    effectiveProductType,
    excludedVariantKeys,
    isEdit,
    product?.id,
    productSkuBase,
    selectedValueIds.length,
    selectedValueIdsByAttribute,
    variants.length,
  ]);

  const onSubmit = async (values: ProductFormValues) => {
    const fallbackVariants = persistedVariantsRef.current.length
      ? persistedVariantsRef.current
      : (product?.variants ?? []);

    const excludedVariantAttributeIds = new Set(
      allAttributes
        .filter(
          (attribute) =>
            isImprintAttribute(attribute) ||
            isStateAttribute(attribute) ||
            isRegionAttribute(attribute),
        )
        .map((attribute) => String(attribute.id)),
    );
    const generatedVariants =
      values.productType === 'variable' && attributeSelectionTouchedRef.current
        ? buildSelectedVariants()
        : [];
    const currentVariants = variants.length ? variants : fallbackVariants;
    const sourceVariants =
      values.productType === 'variable'
        ? generatedVariants.length
          ? mergeVariants(generatedVariants, currentVariants)
          : currentVariants
        : [];
    const sourceVariantsWithoutManagedAttributes = sourceVariants
      .map((variant) => ({
        ...variant,
        attributeValueIds: variant.attributeValueIds.filter((valueId) => {
          const value = allAttributeValues.find((item) => item.id === valueId);
          return !value || !excludedVariantAttributeIds.has(String(value.attributeId));
        }),
        attributeValues: variant.attributeValues?.filter(
          (value) => !excludedVariantAttributeIds.has(String(value.attributeId)),
        ),
      }))
      .map(applyPricingMatrixOverride);
    const sanitizedPersistedAttributeIds = persistedAttributeIdsRef.current.filter(
      (attributeId) => !excludedVariantAttributeIds.has(String(attributeId)),
    );

    const resolvedAttributeIds =
      values.productType === 'variable'
        ? values.attributeIds.length
          ? values.attributeIds
          : derivedAttributeIds.length
            ? derivedAttributeIds
            : sanitizedPersistedAttributeIds.length
              ? sanitizedPersistedAttributeIds
            : deriveAttributeIdsFromVariants(sourceVariantsWithoutManagedAttributes)
        : undefined;

    const resolvedVariants =
      values.productType === 'variable' && sourceVariantsWithoutManagedAttributes.length
        ? sourceVariantsWithoutManagedAttributes.map((variant) =>
            mapVariantPayload(variant, values.stock),
          )
        : undefined;

    if (!isEdit && values.productType === 'variable' && !resolvedVariants?.length) {
      notify.error('Add at least one variant for variable products.');
      return;
    }

    const simplePricing =
      values.productType === 'simple'
        ? syncProductPricing(
            {
              regularPrice: values.regularPrice,
              salePrice: values.salePrice,
              discount: values.discount,
            },
            values.discount != null ? 'discount' : 'salePrice',
            values.discount ?? values.salePrice,
          )
        : null;

    const shippingCharge = hasPrintFormatSelected
      ? Math.max(
          0,
          ...pricingMatrixRows
            .filter((row) => formatIncludesPrint(row.format))
            .map((row) => Number(row.shippingCharge ?? 0)),
        )
      : 0;
    const shippingType: ShippingType = shippingCharge > 0 ? 'paid' : 'free';

    const payload = {
      ...values,
      slug: slugify(values.name) || undefined,
      doiUrl: values.doiUrl.trim(),
      stateId: values.stateId || undefined,
      districtId: values.districtId || undefined,
      assemblyId: values.assemblyId || undefined,
      constituencyId: values.constituencyId || undefined,
      shippingCharge,
      shippingType,
      thumbnailMediaId: pickMediaId(thumbnailMediaId),
      imageMediaIds: pickMediaIds(imageMediaIds),
      ...(simplePricing
        ? {
            regularPrice: simplePricing.regularPrice,
            salePrice: simplePricing.salePrice,
            discount: simplePricing.discount,
          }
        : {}),
      ...(values.productType === 'variable'
        ? {
            ...(resolvedAttributeIds?.length ? { attributeIds: resolvedAttributeIds } : {}),
            ...(resolvedVariants ? { variants: resolvedVariants } : {}),
          }
        : {}),
    };

    if (isEdit) {
      if (!id) return;
      const result = await resolveMutationResult(updateProduct({ id, body: payload }));
      if (result.ok) {
        notify.success(result.message ?? 'Product updated.');
        navigate(ROUTES.PRODUCTS);
        return;
      }
      notify.error(result.message);
      return;
    }

    const result = await resolveMutationResult(createProduct(payload));

    if (result.ok) {
      notify.success(result.message ?? 'Product created.');
      navigate(ROUTES.PRODUCTS);
      return;
    }

    notify.error(result.message);
  };

  const samplePdfUrl = watch('samplePdfUrl');
  const digitalSamplePdfUrl = watch('digitalSamplePdfUrl');
  const contentPdfUrl = watch('contentPdfUrl');

  const handleSamplePdfUpload = async (info: UploadChangeParam) => {
    const file = info.file.originFileObj ?? info.file;
    if (!file || !(file instanceof File)) return;

    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      notify.error('Please upload a PDF file only.');
      return;
    }

    if (file.size > MAX_SAMPLE_PDF_SIZE) {
      notify.error('Sample for PRINT must be 20 MB or smaller.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    const result = await resolveMutationResult<{ samplePdfUrl: string }>(
      uploadSamplePdf(formData),
    );
    if (!result.ok) {
      notify.error(result.message ?? 'Sample for PRINT upload failed.');
      return;
    }

    const uploadedUrl = result.data?.samplePdfUrl ?? '';
    setValue('samplePdfUrl', uploadedUrl, { shouldDirty: true });
    setSamplePdfName(file.name);
    notify.success(result.message ?? 'Sample for PRINT uploaded.');
  };

  const clearSamplePdf = () => {
    setValue('samplePdfUrl', '', { shouldDirty: true });
    setSamplePdfName('');
  };

  const handleDigitalSamplePdfUpload = async (info: UploadChangeParam) => {
    const file = info.file.originFileObj ?? info.file;
    if (!file || !(file instanceof File)) return;

    const lowerName = file.name.toLowerCase();
    const isZip = lowerName.endsWith('.zip');
    const isRar = lowerName.endsWith('.rar');
    if (!isZip && !isRar) {
      notify.error('Please upload a ZIP or RAR archive only.');
      return;
    }

    if (file.size > MAX_DIGITAL_SAMPLE_ARCHIVE_SIZE) {
      notify.error('Sample for eFLIPBOOK and WEBBASED must be 100 MB or smaller.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    const result = await resolveMutationResult<{ digitalSamplePdfUrl: string }>(
      uploadDigitalSamplePdf(formData),
    );
    if (!result.ok) {
      notify.error(result.message ?? 'Sample for eFLIPBOOK and WEBBASED upload failed.');
      return;
    }

    const uploadedUrl = result.data?.digitalSamplePdfUrl ?? '';
    setValue('digitalSamplePdfUrl', uploadedUrl, { shouldDirty: true });
    setDigitalSamplePdfName(file.name);
    notify.success(result.message ?? 'Sample for eFLIPBOOK and WEBBASED uploaded.');
  };

  const clearDigitalSamplePdf = () => {
    setValue('digitalSamplePdfUrl', '', { shouldDirty: true });
    setDigitalSamplePdfName('');
  };

  const handleContentPdfUpload = async (info: UploadChangeParam) => {
    const file = info.file.originFileObj ?? info.file;
    if (!file || !(file instanceof File)) return;

    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      notify.error('Please upload a PDF file only.');
      return;
    }

    if (file.size > MAX_CONTENT_PDF_SIZE) {
      notify.error('Content PDF must be 20 MB or smaller.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    const result = await resolveMutationResult<{ contentPdfUrl: string }>(
      uploadContentPdf(formData),
    );
    if (!result.ok) {
      notify.error(result.message ?? 'Content PDF upload failed.');
      return;
    }

    const uploadedUrl = result.data?.contentPdfUrl ?? '';
    setValue('contentPdfUrl', uploadedUrl, { shouldDirty: true });
    setContentPdfName(file.name);
    notify.success(result.message ?? 'Content PDF uploaded.');
  };

  const clearContentPdf = () => {
    setValue('contentPdfUrl', '', { shouldDirty: true });
    setContentPdfName('');
  };

  const isSaving =
    createState.isLoading ||
    updateState.isLoading ||
    uploadSamplePdfState.isLoading ||
    uploadDigitalSamplePdfState.isLoading ||
    uploadContentPdfState.isLoading;
  useGlobalLoader(isSaving);
  useGlobalLoader(isEdit && isLoadingProduct);

  const thumbnailPinnedItems = useMemo(() => {
    if (!thumbnailMediaId) return [];

    const fromGallery = galleryPreviews.find((item) => item.id === thumbnailMediaId);
    if (fromGallery) return [toPinnedMediaItem(fromGallery)];

    if (thumbnailPreview) {
      return [
        toPinnedMediaItem({
          id: thumbnailMediaId,
          imageSmall: thumbnailPreview,
          imageLarge: thumbnailPreview,
        }),
      ];
    }

    return [];
  }, [galleryPreviews, thumbnailMediaId, thumbnailPreview]);

  const galleryPinnedItems = useMemo(
    () => galleryPreviews.map(toPinnedMediaItem),
    [galleryPreviews],
  );

  return (
    <div className={styles.page}>
      <PageHeader
        actions={
          <>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(ROUTES.PRODUCTS)}>
              Back
            </Button>
            <Button type="primary" loading={isSaving} onClick={handleSubmit(onSubmit)}>
              {isEdit ? 'Save publication' : 'Create publication'}
            </Button>
          </>
        }
      />

      <Card loading={isEdit && isLoadingProduct} className={styles.card}>
        <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
          <Tabs
            items={[
              {
                key: 'basic',
                label: 'Publication Details',
                children: (
                  <>
                    <div className={styles.sectionIntro}>
                      <Typography.Title level={5} className={styles.sectionTitle}>
                        Book identity
                      </Typography.Title>
                      <Typography.Paragraph type="secondary" className={styles.sectionHint}>
                        Core publication metadata shown on the storefront product page.
                      </Typography.Paragraph>
                    </div>
                    <div className={styles.gridThree}>
                      <Controller
                        name="imprintValueId"
                        control={control}
                        rules={{ required: 'Select one imprint' }}
                        render={({ field, fieldState }) => (
                          <Form.Item
                            label="Imprint name"
                            required
                            validateStatus={fieldState.error ? 'error' : ''}
                            help={fieldState.error?.message}
                            className={styles.spanTwo}
                          >
                            <Select
                              {...field}
                              showSearch
                              allowClear
                              optionFilterProp="label"
                              placeholder="Select imprint"
                              options={imprintValueOptions}
                              loading={attributeValuesLoading || attributesLoading}
                              onChange={(value) => {
                                field.onChange(value ?? '');
                                const selected = allAttributeValues.find((item) => item.id === value);
                                const nextImprintName = selected?.name ?? '';
                                setValue('imprint', nextImprintName);
                                setValue('stateId', '');
                                setValue('districtId', '');
                                setValue('assemblyId', '');
                                setValue('constituencyId', '');
                                const kind = resolveFactbookImprintKind(nextImprintName);
                                if (kind === 'election-atlas') {
                                  setValue('name', nextImprintName, {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                  });
                                } else if (
                                  kind === 'district' ||
                                  kind === 'assembly' ||
                                  kind === 'constituency'
                                ) {
                                  setValue('name', '');
                                }
                              }}
                            />
                          </Form.Item>
                        )}
                      />
                      <Controller
                        name="name"
                        control={control}
                        rules={{ required: 'Publication title is required' }}
                        render={({ field, fieldState }) => (
                          <Form.Item
                            label="Publication title"
                            required
                            validateStatus={fieldState.error ? 'error' : ''}
                            help={
                              fieldState.error?.message ||
                              (isElectionAtlasImprint
                                ? 'Fixed to the Election Atlas of India imprint name. Location is not used.'
                                : isTitleManual
                                  ? 'Enter the publication title manually for this imprint.'
                                  : titleKind === 'assembly'
                                    ? 'Auto-filled as {assembly} + selected imprint.'
                                    : titleKind === 'constituency'
                                      ? 'Auto-filled as {constituency} + selected imprint.'
                                      : 'Auto-filled as {district} + selected imprint.')
                            }
                            className={styles.spanTwo}
                          >
                            <Input
                              {...field}
                              disabled={!isTitleManual}
                              readOnly={!isTitleManual}
                              placeholder={
                                isElectionAtlasImprint
                                  ? 'Election Atlas of India'
                                  : isTitleManual
                                    ? 'Enter publication title'
                                    : 'Select imprint and location to auto-fill title'
                              }
                            />
                          </Form.Item>
                        )}
                      />
                      <Form.Item label="Slug">
                        <Input value={autoSlug || 'Auto-generated from title'} disabled />
                      </Form.Item>
                      <Controller
                        name="stateId"
                        control={control}
                        render={({ field }) => (
                          <Form.Item label="State">
                            <Select
                              showSearch
                              allowClear
                              disabled={!stateEnabled}
                              optionFilterProp="label"
                              placeholder={
                                !stateEnabled
                                  ? 'Not required for this imprint'
                                  : 'Select state'
                              }
                              loading={statesLoading}
                              options={stateOptions}
                              value={field.value || undefined}
                              onChange={(value) => {
                                field.onChange(value ?? '');
                                setValue('districtId', '');
                                setValue('assemblyId', '');
                                setValue('constituencyId', '');
                              }}
                            />
                          </Form.Item>
                        )}
                      />
                      <Controller
                        name="districtId"
                        control={control}
                        render={({ field }) => (
                          <Form.Item label="District">
                            <Select
                              showSearch
                              allowClear
                              disabled={!selectedStateId || !districtEnabled}
                              optionFilterProp="label"
                              placeholder={
                                !districtEnabled
                                  ? 'Not required for this imprint'
                                  : selectedStateId
                                    ? 'Select district'
                                    : 'Select state first'
                              }
                              loading={districtsLoading}
                              options={districtOptions}
                              value={field.value || undefined}
                              onChange={(value) => {
                                field.onChange(value ?? '');
                                setValue('constituencyId', '');
                                setValue('assemblyId', '');
                              }}
                            />
                          </Form.Item>
                        )}
                      />
                      <Controller
                        name="constituencyId"
                        control={control}
                        render={({ field }) => (
                          <Form.Item label="Constituency">
                            <Select
                              showSearch
                              allowClear
                              disabled={
                                !constituencyEnabled ||
                                !selectedStateId ||
                                (districtEnabled && !selectedDistrictId)
                              }
                              optionFilterProp="label"
                              placeholder={
                                !constituencyEnabled
                                  ? 'Not required for this imprint'
                                  : !selectedStateId
                                    ? 'Select state first'
                                    : districtEnabled && !selectedDistrictId
                                      ? 'Select district first'
                                      : imprintKind === 'assembly'
                                        ? 'Optional — select constituency'
                                        : 'Select constituency'
                              }
                              loading={constituenciesLoading}
                              options={constituencyOptions}
                              value={field.value || undefined}
                              onChange={(value) => {
                                field.onChange(value ?? '');
                                setValue('assemblyId', '');
                              }}
                            />
                          </Form.Item>
                        )}
                      />
                      <Controller
                        name="assemblyId"
                        control={control}
                        render={({ field }) => (
                          <Form.Item label="Assembly">
                            <Select
                              showSearch
                              allowClear
                              disabled={
                                !assemblyEnabled ||
                                !selectedStateId ||
                                (districtEnabled && !selectedDistrictId)
                              }
                              optionFilterProp="label"
                              placeholder={
                                !assemblyEnabled
                                  ? 'Not required for this imprint'
                                  : !selectedStateId
                                    ? 'Select state first'
                                    : districtEnabled && !selectedDistrictId
                                      ? 'Select district first'
                                      : 'Select assembly'
                              }
                              loading={assembliesLoading}
                              options={assemblyOptions}
                              value={field.value || undefined}
                              onChange={(value) => field.onChange(value ?? '')}
                            />
                          </Form.Item>
                        )}
                      />
                      {/* Series name hidden — imprint is the source of truth for web display/filters.
                      <Controller
                        name="seriesName"
                        control={control}
                        render={({ field }) => (
                          <Form.Item label="Series name">
                            <Input {...field} placeholder="Constituency Factbook™" />
                          </Form.Item>
                        )}
                      />
                      */}
                      <Controller
                        name="seriesNumber"
                        control={control}
                        render={({ field }) => (
                          <Form.Item label="Series number">
                            <Input {...field} placeholder="77" />
                          </Form.Item>
                        )}
                      />
                      <Controller
                        name="editor"
                        control={control}
                        render={({ field }) => (
                          <Form.Item label="Editor">
                            <Input {...field} placeholder="Dr R K Thukral" />
                          </Form.Item>
                        )}
                      />
                      <Controller
                        name="deliveryTime"
                        control={control}
                        render={({ field }) => (
                          <Form.Item label="Expected delivery">
                            <Input {...field} placeholder="Within 5-7 working days in India" />
                          </Form.Item>
                        )}
                      />
                      <Form.Item label="SKU">
                        <Input value={skuPreview || 'Enter title to preview SKU'} disabled />
                      </Form.Item>
                      <Controller
                        name="stock"
                        control={control}
                        render={({ field }) => (
                          <Form.Item label="Stock">
                            <InputNumber
                              min={0}
                              precision={0}
                              className={styles.fullWidth}
                              value={field.value}
                              onChange={(value) => field.onChange(Number(value ?? 0))}
                            />
                          </Form.Item>
                        )}
                      />
                    </div>

                    <div className={styles.sectionIntro}>
                      <Typography.Title level={5} className={styles.sectionTitle}>
                        Catalog mapping
                      </Typography.Title>
                    </div>
                    <div className={styles.gridThree}>
                      <Controller
                        name="brandIds"
                        control={control}
                        rules={{
                          validate: (value) => value.length > 0 || 'Select at least one brand',
                        }}
                        render={({ field, fieldState }) => (
                          <Form.Item
                            label="Brands"
                            required
                            validateStatus={fieldState.error ? 'error' : ''}
                            help={fieldState.error?.message}
                          >
                            <Select
                              {...field}
                              mode="multiple"
                              showSearch
                              optionFilterProp="label"
                              placeholder="Select brands"
                              options={brandOptions}
                              loading={brandsLoading}
                              onOpenChange={(open) => open && void fetchBrands(DROPDOWN_PARAMS)}
                            />
                          </Form.Item>
                        )}
                      />
                      <Controller
                        name="categoryIds"
                        control={control}
                        rules={{
                          validate: (value) => value.length > 0 || 'Select at least one category',
                        }}
                        render={({ field, fieldState }) => (
                          <Form.Item
                            label="Categories"
                            required
                            validateStatus={fieldState.error ? 'error' : ''}
                            help={fieldState.error?.message}
                          >
                            <Select
                              {...field}
                              mode="multiple"
                              showSearch
                              optionFilterProp="label"
                              placeholder="Select categories"
                              options={categoryOptions}
                              loading={categoriesLoading}
                              onOpenChange={(open) => open && void fetchCategories(DROPDOWN_PARAMS)}
                            />
                          </Form.Item>
                        )}
                      />
                      {/* <Controller
                        name="subcategoryIds"
                        control={control}
                        render={({ field }) => (
                          <Form.Item label="Subcategories">
                            <Select
                              {...field}
                              mode="multiple"
                              showSearch
                              optionFilterProp="label"
                              placeholder="Select subcategories"
                              options={subcategoryOptions}
                              loading={subcategoriesLoading}
                              onOpenChange={(open) =>
                                open &&
                                void fetchSubcategories({
                                  ...DROPDOWN_PARAMS,
                                  ...(categoryIds.length ? { categoryIds } : {}),
                                })
                              }
                            />
                          </Form.Item>
                        )}
                      /> */}
                    </div>
                    <div className={styles.gridCatalogFooter}>
                      <Controller
                        name="collectionIds"
                        control={control}
                        render={({ field }) => (
                          <Form.Item label="Collections">
                            <Select
                              {...field}
                              mode="multiple"
                              showSearch
                              optionFilterProp="label"
                              placeholder="Select collections"
                              options={collectionOptions}
                              loading={collectionsLoading}
                              onOpenChange={(open) =>
                                open && void fetchCollections(DROPDOWN_PARAMS)
                              }
                            />
                          </Form.Item>
                        )}
                      />
                      {/* <Controller
                        name="shippingType"
                        control={control}
                        render={({ field }) => (
                          <Form.Item label="Shipping" className={styles.compactField}>
                            <Radio.Group {...field}>
                              <Radio value="free">Free (India)</Radio>
                              <Radio value="paid">Paid</Radio>
                            </Radio.Group>
                          </Form.Item>
                        )}
                      /> */}
                      <Controller
                        name="tags"
                        control={control}
                        render={({ field }) => (
                          <Form.Item label="Tags">
                            <Select {...field} mode="tags" placeholder="factbook, constituency" />
                          </Form.Item>
                        )}
                      />
                      <Controller
                        name="taxClass"
                        control={control}
                        render={({ field }) => (
                          <Form.Item label="Tax class">
                            <Select {...field} options={TAX_CLASS_OPTIONS} />
                          </Form.Item>
                        )}
                      />
                      <Controller
                        name="isActive"
                        control={control}
                        render={({ field }) => (
                          <Form.Item label="Status" className={styles.compactField}>
                            <Switch
                              checked={field.value}
                              onChange={field.onChange}
                              checkedChildren="Active"
                              unCheckedChildren="Inactive"
                              style={{ background: field.value ? BRAND.primary : undefined }}
                            />
                          </Form.Item>
                        )}
                      />
                    </div>
                  </>
                ),
              },
              {
                key: 'content',
                label: 'Publication Content',
                children: (
                  <div className={styles.gridTwo}>
                    <Controller
                      name="shortDescription"
                      control={control}
                      render={({ field }) => (
                        <Form.Item
                          label="Short description"
                          className={styles.full}
                          extra="Shown below the title on the product page"
                        >
                          <Input.TextArea
                            {...field}
                            rows={2}
                            placeholder="Parliamentary-level electoral analysis of Varanasi Parliamentary Constituency, Uttar Pradesh"
                          />
                        </Form.Item>
                      )}
                    />
                    <Controller
                      name="description"
                      control={control}
                      render={({ field }) => (
                        <Form.Item label="About this publication" className={styles.full}>
                          <RichTextEditor
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="About this Constituency Factbook™"
                          />
                        </Form.Item>
                      )}
                    />
                    {/*
                      These sections are now maintained on the selected imprint attribute value:
                      whatsInside -> What's inside
                      editorialStandard -> Editorial note
                      editorialSources -> Contents at a glance
                    */}
                    <Controller
                      name="returnExchangePolicy"
                      control={control}
                      render={({ field }) => (
                        <Form.Item
                          label="Institutional & bulk notes"
                          className={styles.full}
                          extra="Bulk pricing, GeM portal, MARC records, etc."
                        >
                          <RichTextEditor
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Bulk pricing for orders of 10 or more books..."
                          />
                        </Form.Item>
                      )}
                    />
                  </div>
                ),
              },
              {
                key: 'specifications',
                label: 'Specifications',
                children: (
                  <>
                    <div className={styles.sectionIntro}>
                      <Typography.Title level={5} className={styles.sectionTitle}>
                        Book specifications
                      </Typography.Title>
                      <Typography.Paragraph type="secondary" className={styles.sectionHint}>
                        Physical and bibliographic details shown in the storefront specifications panel.
                      </Typography.Paragraph>
                    </div>
                    <div className={styles.gridThree}>
                      <Controller
                        name="formatDetails"
                        control={control}
                        render={({ field }) => (
                          <Form.Item label="Format">
                            <Input {...field} placeholder="Hardbound A4" />
                          </Form.Item>
                        )}
                      />
                      <Controller
                        name="pageCount"
                        control={control}
                        render={({ field }) => (
                          <Form.Item label="Pages">
                            <Input {...field} placeholder="463 + Addendum" />
                          </Form.Item>
                        )}
                      />
                      <Controller
                        name="edition"
                        control={control}
                        render={({ field }) => (
                          <Form.Item label="Edition">
                            <Input {...field} placeholder="2025" />
                          </Form.Item>
                        )}
                      />
                      <Controller
                        name="firstPublished"
                        control={control}
                        render={({ field }) => (
                          <Form.Item label="First published">
                            <Input {...field} placeholder="2018" />
                          </Form.Item>
                        )}
                      />
                      <Controller
                        name="language"
                        control={control}
                        render={({ field }) => (
                          <Form.Item label="Language">
                            <Input {...field} placeholder="English" />
                          </Form.Item>
                        )}
                      />
                      <Controller
                        name="isbn"
                        control={control}
                        render={({ field }) => (
                          <Form.Item label="ISBN-13">
                            <Input {...field} placeholder="978-93-87320-77-4" />
                          </Form.Item>
                        )}
                      />
                      <Controller
                        name="doiUrl"
                        control={control}
                        rules={{
                          required: 'DOI URL is required',
                          pattern: {
                            value: /^https?:\/\/\S+$/i,
                            message: 'Enter a valid DOI URL',
                          },
                        }}
                        render={({ field, fieldState }) => (
                          <Form.Item
                            label="DOI URL"
                            required
                            validateStatus={fieldState.error ? 'error' : ''}
                            help={fieldState.error?.message}
                          >
                            <Input {...field} placeholder="https://doi.org/10.1234/example" />
                          </Form.Item>
                        )}
                      />
                      <Controller
                        name="weight"
                        control={control}
                        render={({ field }) => (
                          <Form.Item label="Weight (approx.)">
                            <Input {...field} placeholder="1.5 kg" />
                          </Form.Item>
                        )}
                      />
                      <Controller
                        name="dimensions"
                        control={control}
                        render={({ field }) => (
                          <Form.Item label="Dimensions">
                            <Input {...field} placeholder="29 × 21 × 3 cm" />
                          </Form.Item>
                        )}
                      />
                    </div>
                  </>
                ),
              },
              /* Cite This Book — temporarily hidden from admin product tabs
              {
                key: 'citation',
                label: 'Cite This Book',
                children: (
                  <div className={styles.gridTwo}>
                    <Controller
                      name="apaCitation"
                      control={control}
                      render={({ field }) => (
                        <Form.Item
                          label="APA · 7th ed."
                          className={styles.full}
                          extra="Shown in the citation panel on the product page"
                        >
                          <Input.TextArea
                            {...field}
                            rows={4}
                            placeholder="Indiastat Publications. (2024). Varanasi Constituency Factbook (7th ed.). New Delhi: Datanet India."
                          />
                        </Form.Item>
                      )}
                    />
                    <Controller
                      name="chicagoCitation"
                      control={control}
                      render={({ field }) => (
                        <Form.Item label="Chicago · 17th ed." className={styles.full}>
                          <Input.TextArea
                            {...field}
                            rows={4}
                            placeholder="Indiastat Publications. Varanasi Constituency Factbook. 7th ed. New Delhi: Datanet India Pvt. Ltd., 2024."
                          />
                        </Form.Item>
                      )}
                    />
                  </div>
                ),
              },
              */
              /* {
                key: 'pricing',
                label: 'Pricing & Inventory',
                children:
                  productType === 'simple' ? (
                    <div className={styles.grid}>
                      <Controller
                        name="regularPrice"
                        control={control}
                        rules={{ required: 'Regular price is required' }}
                        render={({ field, fieldState }) => (
                          <Form.Item
                            label="Regular price"
                            required
                            validateStatus={fieldState.error ? 'error' : ''}
                            help={fieldState.error?.message}
                          >
                            <InputNumber
                              min={0}
                              precision={0}
                              className={styles.fullWidth}
                              prefix="₹"
                              value={field.value}
                              onChange={(value) => applySimplePricing('regularPrice', value)}
                            />
                          </Form.Item>
                        )}
                      />
                      <Controller
                        name="discount"
                        control={control}
                        render={({ field }) => (
                          <Form.Item label="Discount" extra="Percentage off regular price">
                            <InputNumber
                              min={0}
                              max={100}
                              className={styles.fullWidth}
                              suffix="%"
                              value={field.value}
                              onChange={(value) => applySimplePricing('discount', value)}
                            />
                          </Form.Item>
                        )}
                      />
                      <Controller
                        name="salePrice"
                        control={control}
                        render={({ field }) => (
                          <Form.Item
                            label="Sale price"
                            extra="Auto-calculated from discount, or enter manually"
                          >
                            <InputNumber
                              min={0}
                              precision={0}
                              className={styles.fullWidth}
                              prefix="₹"
                              value={field.value}
                              onChange={(value) => applySimplePricing('salePrice', value)}
                            />
                          </Form.Item>
                        )}
                      />
                      <Controller
                        name="costPrice"
                        control={control}
                        render={({ field }) => (
                          <Form.Item label="Cost price">
                            <InputNumber
                              {...field}
                              min={0}
                              precision={0}
                              className={styles.fullWidth}
                              prefix="₹"
                            />
                          </Form.Item>
                        )}
                      />
                      <Controller
                        name="stock"
                        control={control}
                        render={({ field }) => (
                          <Form.Item label="Stock">
                            <InputNumber {...field} min={0} className={styles.fullWidth} />
                          </Form.Item>
                        )}
                      />
                      <Controller
                        name="trackInventory"
                        control={control}
                        render={({ field }) => (
                          <Form.Item label="Track inventory">
                            <Switch checked={field.value} onChange={field.onChange} />
                          </Form.Item>
                        )}
                      />
                    </div>
                  ) : (
                    <Typography.Paragraph type="secondary">
                      Pricing and stock are managed per variant in the Attributes & Variants tab.
                    </Typography.Paragraph>
                  ),
              }, */
              {
                key: 'variants',
                label: 'Attributes & Variants',
                children: (
                  <div className={styles.variantSection}>
                    {attributesLoadError ? (
                      <Alert
                        type="error"
                        showIcon
                        message="Could not load attributes"
                        description="The API cannot reach MongoDB. Check api/.env MONGODB_URI and restart the API server."
                        action={
                          <Button size="small" onClick={() => void refetchAttributes()}>
                            Retry
                          </Button>
                        }
                      />
                    ) : null}
                    {attributeValuesLoadError ? (
                      <Alert
                        type="error"
                        showIcon
                        message="Could not load attribute values"
                        description="Failed to load values from Attribute Values. Check your connection and try again."
                        action={
                          <Button size="small" onClick={() => void refetchAttributeValues()}>
                            Retry
                          </Button>
                        }
                      />
                    ) : null}

                    <Form.Item>
                      <AttributeValuePicker
                        groups={attributePickerGroups}
                        selectedValueIds={selectedValueIds}
                        loading={attributesLoading || attributeValuesLoading}
                        disabled={
                          (attributesLoading || attributeValuesLoading) &&
                          !selectedValueIds.length &&
                          !attributePickerGroups.length
                        }
                        onChange={(valueIds) => {
                          attributeSelectionTouchedRef.current = true;
                          setSelectedValueIds(valueIds);
                          setExcludedVariantKeys([]);
                        }}
                      />
                    </Form.Item>
                  </div>
                ),
              },
              {
                key: 'pricing',
                label: 'Pricing',
                children: (
                  <div className={styles.variantSection}>
                    <div className={styles.sectionIntro}>
                      <Typography.Title level={5} className={styles.sectionTitle}>
                        Price matrix
                      </Typography.Title>
                      <Typography.Paragraph type="secondary" className={styles.sectionHint}>
                        Add prices for each selected Price Category and Format / Access Type
                        combination. Shipping is set separately on each Print format row (e.g.
                        Print, Print + eFlipbook).
                      </Typography.Paragraph>
                    </div>
                    {!pricingMatrixRows.length ? (
                      <Alert
                        type="warning"
                        showIcon
                        message="Select price category and format options first"
                        description="Go to Attributes & Variants and select values under Price Category and Format / Access Type to generate pricing rows."
                      />
                    ) : null}
                    {!hasPrintFormatSelected && pricingMatrixRows.length ? (
                      <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 12 }}
                        message="No Print format selected"
                        description="Shipping is available only for Format / Access Type options that include Print."
                      />
                    ) : null}
                    <Table
                      rowKey={(record) => record.key}
                      size="small"
                      className={`${styles.variantTable} ${styles.pricingTable}`}
                      columns={pricingColumns}
                      dataSource={pricingMatrixRows}
                      pagination={false}
                      scroll={{ x: 980 }}
                      tableLayout="fixed"
                    />
                  </div>
                ),
              },
              {
                key: 'images',
                label: 'Cover & Media',
                children: (
                  <div className={styles.imagesSection}>
                    <div>
                      <Typography.Text strong>Cover image</Typography.Text>
                      {/factpage/i.test(resolvedImprintName) ? (
                        <Typography.Paragraph type="secondary" className={styles.fieldHint}>
                          On save, this file is stored as{' '}
                          <code>{'{District}-District-Factpage.png'}</code> under{' '}
                          <code>/uploads/images/District_Factpage/{'{State}'}/</code>. Select
                          state and district first.
                        </Typography.Paragraph>
                      ) : null}
                      <div className={styles.thumbRow}>
                        <button
                          type="button"
                          className={styles.thumbBox}
                          onClick={() => setThumbPickerOpen(true)}
                        >
                          {thumbnailPreview ? (
                            <img
                              src={getMediaImageUrl(thumbnailPreview) ?? thumbnailPreview}
                              alt=""
                            />
                          ) : (
                            <PictureOutlined />
                          )}
                        </button>
                        {thumbnailMediaId ? (
                          <Button
                            danger
                            type="text"
                            icon={<DeleteOutlined />}
                            onClick={() => {
                              setThumbnailMediaId(undefined);
                              setThumbnailPreview(undefined);
                            }}
                          />
                        ) : null}
                      </div>
                    </div>
                    <div>
                      <Typography.Text strong>Gallery & sample pages</Typography.Text>
                      <div className={styles.galleryGrid}>
                        {galleryPreviews.map((item) => (
                          <div key={item.id} className={styles.galleryItem}>
                            <img
                              src={getMediaImageUrl(item.imageLarge ?? item.imageSmall)}
                              alt=""
                            />
                            <Button
                              size="small"
                              danger
                              type="text"
                              icon={<DeleteOutlined />}
                              onClick={() => {
                                setImageMediaIds((ids) =>
                                  ids.filter((mediaId) => mediaId !== item.id),
                                );
                                setGalleryPreviews((items) =>
                                  items.filter((media) => media.id !== item.id),
                                );
                              }}
                            />
                          </div>
                        ))}
                        <button
                          type="button"
                          className={styles.addImage}
                          onClick={() => setGalleryPickerOpen(true)}
                        >
                          <PlusOutlined />
                        </button>
                      </div>
                    </div>
                    <div>
                      <Typography.Text strong>Sample for PRINT</Typography.Text>
                      <Typography.Paragraph type="secondary" className={styles.fieldHint}>
                        Upload sample plates PDF shown on the product page.
                      </Typography.Paragraph>
                      <div className={styles.pdfUploadRow}>
                        {samplePdfUrl ? (
                          <div className={styles.pdfFileCard}>
                            <FilePdfOutlined className={styles.pdfFileIcon} />
                            <div className={styles.pdfFileMeta}>
                              <span className={styles.pdfFileName}>
                                {samplePdfName || 'Sample for PRINT'}
                              </span>
                              <a
                                href={getSamplePdfUrl(samplePdfUrl)}
                                target="_blank"
                                rel="noreferrer"
                                className={styles.pdfFileLink}
                              >
                                Preview PDF
                              </a>
                            </div>
                            <Button
                              danger
                              type="text"
                              icon={<DeleteOutlined />}
                              onClick={clearSamplePdf}
                            />
                          </div>
                        ) : (
                          <Upload
                            accept=".pdf,application/pdf"
                            showUploadList={false}
                            beforeUpload={() => false}
                            onChange={(info) => void handleSamplePdfUpload(info)}
                          >
                            <Button
                              icon={<FilePdfOutlined />}
                              loading={uploadSamplePdfState.isLoading}
                            >
                              Upload sample for PRINT
                            </Button>
                          </Upload>
                        )}
                      </div>
                    </div>
                    <div>
                      <Typography.Text strong>Sample for eFLIPBOOK and WEBBASED</Typography.Text>
                      <div className={styles.pdfUploadRow}>
                        {digitalSamplePdfUrl ? (
                          <div className={styles.pdfFileCard}>
                            <FileZipOutlined className={styles.pdfFileIcon} />
                            <div className={styles.pdfFileMeta}>
                              <span className={styles.pdfFileName}>
                                {digitalSamplePdfName || 'Sample for eFLIPBOOK and WEBBASED'}
                              </span>
                              <a
                                href={getDigitalSamplePdfUrl(digitalSamplePdfUrl)}
                                target="_blank"
                                rel="noreferrer"
                                className={styles.pdfFileLink}
                              >
                                Open HTML sample
                              </a>
                            </div>
                            <Button
                              danger
                              type="text"
                              icon={<DeleteOutlined />}
                              onClick={clearDigitalSamplePdf}
                            />
                          </div>
                        ) : (
                          <Upload
                            accept=".zip,.rar,application/zip,application/x-zip-compressed,application/vnd.rar,application/x-rar-compressed"
                            showUploadList={false}
                            beforeUpload={() => false}
                            onChange={(info) => void handleDigitalSamplePdfUpload(info)}
                          >
                            <Button
                              icon={<FileZipOutlined />}
                              loading={uploadDigitalSamplePdfState.isLoading}
                            >
                              Upload ZIP / RAR sample
                            </Button>
                          </Upload>
                        )}
                      </div>
                    </div>
                    <div>
                      <Typography.Text strong>Content PDF</Typography.Text>
                      <Typography.Paragraph type="secondary" className={styles.fieldHint}>
                        Upload content PDF opened by the Content button on the product page.
                      </Typography.Paragraph>
                      <div className={styles.pdfUploadRow}>
                        {contentPdfUrl ? (
                          <div className={styles.pdfFileCard}>
                            <FilePdfOutlined className={styles.pdfFileIcon} />
                            <div className={styles.pdfFileMeta}>
                              <span className={styles.pdfFileName}>
                                {contentPdfName || 'Content PDF'}
                              </span>
                              <a
                                href={getContentPdfUrl(contentPdfUrl)}
                                target="_blank"
                                rel="noreferrer"
                                className={styles.pdfFileLink}
                              >
                                Preview PDF
                              </a>
                            </div>
                            <Button
                              danger
                              type="text"
                              icon={<DeleteOutlined />}
                              onClick={clearContentPdf}
                            />
                          </div>
                        ) : (
                          <Upload
                            accept=".pdf,application/pdf"
                            showUploadList={false}
                            beforeUpload={() => false}
                            onChange={(info) => void handleContentPdfUpload(info)}
                          >
                            <Button
                              icon={<FilePdfOutlined />}
                              loading={uploadContentPdfState.isLoading}
                            >
                              Upload content PDF
                            </Button>
                          </Upload>
                        )}
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                key: 'seo',
                label: 'SEO',
                children: (
                  <div className={styles.grid}>
                    <Controller
                      name="metaTitle"
                      control={control}
                      render={({ field }) => (
                        <Form.Item label="Meta title" className={styles.full}>
                          <Input {...field} />
                        </Form.Item>
                      )}
                    />
                    <Controller
                      name="metaDescription"
                      control={control}
                      render={({ field }) => (
                        <Form.Item label="Meta description" className={styles.full}>
                          <Input.TextArea {...field} rows={3} />
                        </Form.Item>
                      )}
                    />
                    <Controller
                      name="metaKeywords"
                      control={control}
                      render={({ field }) => (
                        <Form.Item label="Keywords" className={styles.full}>
                          <Select {...field} mode="tags" placeholder="Add keywords" />
                        </Form.Item>
                      )}
                    />
                  </div>
                ),
              },
            ]}
          />
        </Form>
      </Card>

      <MediaPickerModal
        open={thumbPickerOpen}
        value={thumbnailMediaId ? [thumbnailMediaId] : []}
        pinnedItems={thumbnailPinnedItems}
        onClose={() => setThumbPickerOpen(false)}
        onChange={(ids, items) => {
          setThumbnailMediaId(ids[0]);
          setThumbnailPreview(getMediaImageUrl(items[0]?.imageLarge ?? items[0]?.imageSmall));
        }}
      />
      <MediaPickerModal
        open={galleryPickerOpen}
        multiple
        value={imageMediaIds}
        pinnedItems={galleryPinnedItems}
        onClose={() => setGalleryPickerOpen(false)}
        onChange={(ids, items) => {
          setImageMediaIds(ids);
          setGalleryPreviews(
            items.map((item) => ({
              id: item.id,
              imageSmall: item.imageSmall,
              imageLarge: item.imageLarge,
            })),
          );
        }}
      />
    </div>
  );
}
